/**
 * Direct axios calls to the admin/* CRUD endpoints. We bypass the generated
 * SDK here so the page works before the backend OpenAPI spec is regenerated;
 * once the SDK is refreshed these can be swapped for the typed services.
 */
import { api } from "../../api_client";

const BASE = "/api/v1";

export interface RefRow {
  id: number;
  name: string;
  [key: string]: unknown;
}

export interface AttributeRow {
  id: number;
  name: string;
  displayType: string;
  valueIds: number[];
}

export interface AttributeValueRow {
  id: number;
  name: string;
  attributeId: number;
}

const buildSimpleCrud = (path: string) => ({
  list: async (): Promise<RefRow[]> => (await api.get(`${BASE}/${path}`)).data,
  create: async (input: { name: string }) =>
    (await api.post(`${BASE}/admin/${path}`, input)).data,
  update: async (id: number, input: Partial<{ name: string }>) =>
    (await api.patch(`${BASE}/admin/${path}/${id}`, input)).data,
  remove: async (id: number) => {
    await api.delete(`${BASE}/admin/${path}/${id}`);
  },
});

export const brandsCrud = {
  ...buildSimpleCrud("brands"),
  create: async (input: { name: string; logo?: string | null }) =>
    (await api.post(`${BASE}/admin/brands`, input)).data,
  update: async (
    id: number,
    input: Partial<{ name: string; logo: string | null }>,
  ) => (await api.patch(`${BASE}/admin/brands/${id}`, input)).data,
};

export interface CategoryRow {
  id: number;
  name: string;
  completeName?: string;
  displayName?: string;
  parent?: { id: number; name: string } | null;
}

export const categoriesCrud = {
  list: async (): Promise<CategoryRow[]> =>
    (await api.get(`${BASE}/categories`)).data,
  create: async (input: { name: string; parentId?: number | null }) =>
    (await api.post(`${BASE}/admin/categories`, input)).data,
  update: async (id: number, input: Partial<{ name: string; parentId: number | null }>) =>
    (await api.patch(`${BASE}/admin/categories/${id}`, input)).data,
  remove: async (id: number) => {
    await api.delete(`${BASE}/admin/categories/${id}`);
  },
};

export const warrantiesCrud = buildSimpleCrud("product-warranties");
export const warrantyTypesCrud = buildSimpleCrud("product-warranty-types");
export const warrantyProvidersCrud = buildSimpleCrud("product-warranty-providers");

export interface WarrantyProductRow {
  id: number;
  name: string;
  price: number | null;
  warrantyId: number | null;
  warrantyTypeId: number | null;
  warrantyProviderId: number | null;
  description: string | null;
  /** product.supplierinfo row ids (used to look up the owning seller). */
  sellerIds: number[];
}

/**
 * Admin moderation surface only. Warranty SKUs are seller-owned (created via
 * /my-warranties); admin can list cross-seller and archive (DELETE) for
 * takedowns. PATCH stays available for ad-hoc moderation overrides.
 */
export const warrantyProductsCrud = {
  list: async (): Promise<WarrantyProductRow[]> =>
    (await api.get(`${BASE}/admin/warranty-products`)).data,
  update: async (
    id: number,
    input: Partial<{
      name: string;
      price: number;
      description: string;
      active: boolean;
    }>,
  ) => (await api.patch(`${BASE}/admin/warranty-products/${id}`, input)).data,
  remove: async (id: number) => {
    await api.delete(`${BASE}/admin/warranty-products/${id}`);
  },
};
export const dealTypesCrud = buildSimpleCrud("product-deal-types");
export const conditionsCrud = buildSimpleCrud("product-conditions");
export const screenSizesCrud = buildSimpleCrud("product-screen-sizes");
export const resolutionsCrud = buildSimpleCrud("product-resolutions");

export const attributesCrud = {
  list: async (): Promise<AttributeRow[]> =>
    (await api.get(`${BASE}/product-attributes`)).data,
  create: async (input: { name: string; displayType?: string }) =>
    (await api.post(`${BASE}/admin/product-attributes`, input)).data,
  update: async (id: number, input: Partial<{ name: string; displayType: string }>) =>
    (await api.patch(`${BASE}/admin/product-attributes/${id}`, input)).data,
  remove: async (id: number) => {
    await api.delete(`${BASE}/admin/product-attributes/${id}`);
  },
};

export const attributeValuesCrud = {
  listByAttribute: async (attributeId: number): Promise<AttributeValueRow[]> =>
    (await api.get(`${BASE}/product-attributes/${attributeId}/values`)).data,
  create: async (input: { name: string; attributeId: number }) =>
    (await api.post(`${BASE}/admin/product-attributes/values`, input)).data,
  update: async (id: number, input: { name?: string }) =>
    (await api.patch(`${BASE}/admin/product-attributes/values/${id}`, input)).data,
  remove: async (id: number) => {
    await api.delete(`${BASE}/admin/product-attributes/values/${id}`);
  },
};
